// ============================================================
// TILT — SOLO SCREEN
// ------------------------------------------------------------
// One table, one player. Serves BOTH score tables and trial
// (puzzle) tables with zero forks — the difference between them
// lives entirely in the table's `objectives` array, which the
// engine already handles generically.
//
// That's the payoff of building the objective machinery into the
// engine instead of into a "puzzle mode": a puzzle table is just
// a table that declares what winning means.
// ============================================================

import { useEffect, useRef } from "react";
import { PHASE } from "../TiltEngine";
import { UI, PF, PAGE_BG, PANEL, facet, facetTB, RAISED, glow, ensureKeyframes } from "../theme";
// ★ THE 3D DECK. Playfield3D reads the same snapshot the 2D
// renderer read and answers the same props — the swap is one
// import line, which was the entire point of keeping the render
// surface a pure function of the snapshot.
import Playfield from "../Playfield3D";
import useTiltEngine from "../useTiltEngine";
import { captureTable, restoreTable } from "../tiltRelay";
import {
  ScoreBoard, ChargeMeter, StatusStrip, PowerBar,
  ObjectivePanel, Ticker, Plunger, Banner, BossBar, btnStyle,
} from "../HUD";

// relay (QUICK CO-OP, optional): { before, onBallOver(snap) } - this screen plays exactly ONE ball, starting
// from the shared table `before`, and hands the table back when it drains (or when you walk away from it).
export default function SoloScreen({ table, loadout, onExit, onResult, relay }) {
  ensureKeyframes();

  // ── D6: lifetime stats. Counted here, committed ONCE with the
  //    result — same one-event-one-commit law as App.push.
  const statsRef = useRef({ launches: 0, drains: 0 });

  const {
    engineRef, snap, press, cast, beginAim, cancelAim,
    restart: engineRestart, plungerDown, plungerUp,
  } = useTiltEngine(table, {
    loadout: loadout || table.loadout,
    onReady: relay ? (e) => { restoreTable(e, relay.before); e.ballsLeft = 1; } : undefined,
    onEvent: (ev) => {
      if (relay) {                                   // a relay ball: no solo bests or tokens, just hand the table on
        if (ev.type === "over") handOn();
        return;
      }
      if (ev.type === "launch") statsRef.current.launches += 1;
      if (ev.type === "drain")  statsRef.current.drains += 1;
      if (ev.type === "over" || ev.type === "cleared") {
        onResult?.({
          table: table.id,
          score: ev.score,
          cleared: ev.type === "cleared",
          stats: { ...statsRef.current },
        });
        // The result owns these counts now. A restart-less replay
        // (cleared → keep playing) must not re-commit them.
        statsRef.current = { launches: 0, drains: 0 };
      }
    },
  });

  // A restart is a NEW game — its stats start at zero, or the
  // abandoned game's launches would ride into the next result.
  // QUICK CO-OP: hand the table on exactly once - when the ball drains, or when you walk away mid-ball
  // (walking away spends the ball; everything you scored on it still counts for the team)
  const handed = useRef(false);
  const handOn = () => {
    const e = engineRef.current;
    if (!relay || handed.current || !e) return;
    handed.current = true;
    // walking away mid-ball counts as the drain it replaces: Willie's guard refills, as a drain refills it
    if (e.phase !== PHASE.OVER && e.boss && !e.boss.felled) {
      e.boss.lyf = e.boss.lyfMax;
      e.boss.fractured = e.boss.lyfMax === 0;
    }
    relay.onBallOver(captureTable(e));
  };
  // a relay ball can never hold the run hostage (review): not launched in 45 s -> it launches itself;
  // the tab hidden for 30 s (a backgrounded tab stops the table) -> the ball is handed on
  useEffect(() => {
    if (!relay) return undefined;
    const launch = setTimeout(() => {
      const e = engineRef.current;
      if (e && e.phase === PHASE.READY) e.release();
    }, 45000);
    let away = null;
    const vis = () => {
      if (document.hidden) away = setTimeout(handOn, 30000);
      else { clearTimeout(away); away = null; }
    };
    document.addEventListener("visibilitychange", vis);
    return () => { clearTimeout(launch); clearTimeout(away); document.removeEventListener("visibilitychange", vis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exit = relay ? handOn : onExit;

  const restart = () => {
    statsRef.current = { launches: 0, drains: 0 };
    engineRestart();
  };

  // Aim resolution — a click on the playfield while a power is armed.
  const handleAim = (point) => {
    const id = snap?.aiming;
    if (!id) return;
    cast(id, point);
  };

  if (!snap) return null;

  const isTrial = table.kind === "trial";

  return (
    // ── IT MUST FIT ON THE SCREEN. ────────────────────────────
    //
    // The table is 400 × 700 table-units, and the Playfield canvas
    // holds that aspect ratio faithfully. So a 420px-wide table is a
    // 735px-TALL table — and with page padding on top of it, the
    // cabinet was taller than the window. Every session opened with
    // an inch of scroll, and the bottom of the playfield (where the
    // flippers are, and therefore where the entire game happens) sat
    // below the fold.
    //
    // The fix is to stop sizing the table by WIDTH. A pinball table
    // is a tall thing in a short window: the binding constraint is
    // always the HEIGHT, and the width should fall out of it.
    //
    // So the whole cabinet is pinned to `100dvh` — DYNAMIC viewport
    // height, which is the one that accounts for a mobile browser's
    // disappearing address bar (`100vh` lies on iOS Safari and will
    // hand you a chrome-bar's worth of scroll back). The table then
    // takes the height it's given and derives its own width from the
    // aspect ratio.
    //
    // `overflow: hidden` on the shell is the belt to that braces: if
    // anything ever does overflow again, it gets clipped rather than
    // silently adding a scrollbar nobody notices in a screenshot.
    <div style={{
      height: "100dvh", maxHeight: "100dvh", background: PAGE_BG,
      display: "flex", justifyContent: "center", alignItems: "center",
      padding: "10px 12px",
      boxSizing: "border-box",
      overflow: "hidden",
      fontFamily: UI.font,
    }}>
      <div style={{
        display: "flex", gap: 18, alignItems: "stretch",
        // ── LOCKED-IN CABINET WIDTH ──────────────────────────
        // 1440, not 900. The 3D table is tall; at 900 the center
        // bay gets pinched and the whole cabinet reads collapsed.
        // Do not lower this without re-checking the table fill.
        maxWidth: 1440, width: "100%", height: "100%",
        minHeight: 0,          // ← lets a flex child actually shrink
      }}>

        {/* ── LEFT RAIL — the cabinet's chrome ─────────────── */}
        {/* The rail scrolls INTERNALLY if it ever outgrows the window.
            It must never be the thing that makes the PAGE scroll — the
            table has to stay put. `minHeight: 0` is the incantation that
            actually lets a flex child shrink below its content; without
            it the rail silently refuses to compress and shoves the whole
            cabinet down the page. */}
        <div style={{
          width: 208, flexShrink: 0, display: "flex",
          flexDirection: "column", gap: 10,
          minHeight: 0, overflowY: "auto", overflowX: "hidden",
        }}>
          <div>
            <div style={{
              fontFamily: UI.fontD, fontSize: 17, fontWeight: 900,
              letterSpacing: 3, color: UI.brassBright,
              textShadow: glow(UI.brassBright),
            }}>
              {table.name.toUpperCase()}
            </div>
            <div style={{
              fontSize: 10, color: UI.textFaint, letterSpacing: 1,
              marginTop: 2, fontStyle: "italic",
            }}>
              {table.blurb}
            </div>
          </div>

          <ScoreBoard snap={snap} accent={isTrial ? UI.good : UI.cool} />

          <div style={{
            background: PANEL, clipPath: facet(8), padding: "10px 12px",
            boxShadow: RAISED, display: "flex", flexDirection: "column", gap: 10,
          }}>
            <ChargeMeter snap={snap} />
            <StatusStrip snap={snap} />
          </div>

          {/* ★ THE BEING — the table's keeper, if it has one. */}
          {snap.boss && <BossBar snap={snap} label={table.boss?.title || "THE KEEPER"} />}

          {isTrial && <ObjectivePanel snap={snap} />}

          <div style={{
            background: PANEL, clipPath: facet(8), padding: "8px 12px",
            boxShadow: RAISED,
          }}>
            <Ticker snap={snap} />
          </div>

          <button onClick={exit} style={{ ...btnStyle(UI.chromeDim), padding: "8px 14px" }}>
            ← EXIT
          </button>
        </div>

        {/* ── THE TABLE ────────────────────────────────────── */}
        {/* THE TABLE. Sized by HEIGHT — `aspectRatio` derives the width.
            This is the whole scroll fix in one line: the cabinet's tall
            dimension is the one the window actually constrains. */}
        <div style={{
          flex: 1, position: "relative",
          height: "100%", minHeight: 0,
          // FULL-HEIGHT TABLE BAY — the bay takes the entire window
          // height and aspectRatio derives the width from it. The old
          // `maxWidth: 420` cap is deliberately GONE: it was sized for
          // the flat 2D board and it strangles the 3D table into a
          // skinny strip. Height is the only constraint that matters.
          aspectRatio: `${table.W} / ${table.H}`,
          margin: "0 auto",
          display: "flex", flexDirection: "column",
        }}>
          <Playfield
            engineRef={engineRef}
            snap={snap}
            aiming={snap.aiming}
            onAim={handleAim}
          />
          <Banner
            snap={snap}
            onRestart={restart}
            onExit={exit}
          />

          {/* Aim-mode overlay — the player must know they're armed. */}
          {snap.aiming && (
            <div style={{
              position: "absolute", top: 10, left: 0, right: 0,
              textAlign: "center", pointerEvents: "none",
            }}>
              <span style={{
                background: "rgba(4,5,10,0.85)",
                padding: "6px 14px", clipPath: facetTB(5),
                fontFamily: UI.font, fontSize: 11, letterSpacing: 2,
                color: UI.brassBright, textShadow: glow(UI.brassBright),
                animation: "tilt-pulse 1s ease-in-out infinite",
              }}>
                TAP THE TABLE TO PLACE · ESC TO CANCEL
              </span>
            </div>
          )}
        </div>

        {/* ── RIGHT RAIL — powers + controls ───────────────── */}
        <div style={{
          width: 160, flexShrink: 0, display: "flex",
          flexDirection: "column", gap: 12,
        }}>
          <PowerBar
            snap={snap}
            onCast={cast}
            onAimStart={beginAim}
          />

          <Plunger snap={snap} onDown={plungerDown} onUp={plungerUp} />

          {/* Touch flippers — mobile. Big, dumb, thumb-shaped. */}
          <div style={{ display: "flex", gap: 8 }}>
            <FlipButton label="◀" onDown={() => press("L", true)} onUp={() => press("L", false)} />
            <FlipButton label="▶" onDown={() => press("R", true)} onUp={() => press("R", false)} />
          </div>

          <div style={{
            fontFamily: UI.fontNum, fontSize: 9, color: UI.textFaint,
            lineHeight: 1.7, marginTop: 4,
          }}>
            <div>◀ ▶ / A D — flippers</div>
            <div>SPACE — plunger</div>
            <div>1-4 — powers</div>
            <div>N / ↑ — nudge</div>
            <div>ESC — cancel aim</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FlipButton({ label, onDown, onUp }) {
  return (
    <button
      onMouseDown={onDown} onMouseUp={onUp} onMouseLeave={onUp}
      onTouchStart={(e) => { e.preventDefault(); onDown(); }}
      onTouchEnd={(e) => { e.preventDefault(); onUp(); }}
      style={{
        flex: 1, padding: "16px 0", clipPath: facetTB(6),
        background: PANEL, border: "none",
        color: PF.flipper, fontSize: 18, cursor: "pointer",
        boxShadow: RAISED, userSelect: "none", WebkitUserSelect: "none",
        touchAction: "none",
      }}
    >
      {label}
    </button>
  );
}
